import { useState } from 'react';
import { api } from '../api';
import { useApi } from '../lib/useApi';
import { Card, CardHead, ErrorState, Loading, PlanBadge } from '../components/ui';
import { fmtDate, fmtTime, gb, n0, plural, rub } from '../lib/format';
import { useAuth } from '../components/AuthProvider';

/* Расписание фоновых задач известно из кода бэкенда; отдельного эндпоинта,
   который бы его отдавал, нет — поэтому список зашит здесь. */
const JOBS = [
  { key: 'subscription/auto-renew', title: 'продление подписок', schedule: 'каждый день 03:00' },
  { key: 'subscription/expire', title: 'завершение истёкших', schedule: 'каждый день 03:10' },
  { key: 'subscription/activate-scheduled', title: 'включение отложенных', schedule: 'каждый день 03:20' },
  { key: 'subscription/cancel-stale-unpaid', title: 'отмена зависших неоплаченных', schedule: 'каждый день 03:30' },
  { key: 'subscription/process-trial-expirations', title: 'обработка окончаний триала', schedule: 'каждый день 04:00' },
  { key: 'subscription/trial-expiration-warnings-3-days', title: 'письма «триал кончается через 3 дня»', schedule: 'каждый день 09:00' },
  { key: 'subscription/trial-expiration-warnings-1-day', title: 'письма «триал кончается завтра»', schedule: 'каждый день 09:05' },
  { key: 'retention/tick', title: 'удаление данных по сроку', schedule: 'каждый день 05:00' },
  { key: 'notification/batch-flush', title: 'отправка накопленных уведомлений', schedule: 'каждые 15 минут' },
];

export default function Ops({ nonce }) {
  const plans = useApi(() => api.plans(), [nonce]);
  const { canEdit } = useAuth();

  return (
    <>
      <div className="grid cards-2">
        <Card className="pad">
          <CardHead title="тарифы" caption="все рабочие тарифы, включая те, которых нет на сайте" />
          {plans.loading ? <Loading what="тарифы" />
            : plans.error ? <ErrorState error={plans.error} onRetry={plans.reload} />
            : (
              <div className="list">
                {plans.data.map((p) => (
                  <div className="row" key={p.id}>
                    <span className="rn">
                      <PlanBadge plan={p.name} published={p.published} note={p.hiddenNote} />
                      <div className="t-sub">
                        {p.users} {plural(p.users, 'место', 'места', 'мест')} · {gb(p.spaceMb)} ·{' '}
                        {p.projects || '∞'} {plural(p.projects || 0, 'проект', 'проекта', 'проектов')}
                        {p.trialDays ? ` · триал ${p.trialDays} ${plural(p.trialDays, 'день', 'дня', 'дней')}` : ' · без триала'}
                      </div>
                    </span>
                    <span className="rv">{p.price ? rub(p.price) + '/мес' : 'бесплатно'} · {n0(p.workspaces)} РП</span>
                  </div>
                ))}
              </div>
            )}
          <div className="note" style={{ marginTop: 12 }}>
            Тариф работает, даже если он не опубликован: подписку на него выдают по прямой ссылке,
            а на сайте его не видно. Список здесь всегда полный.
          </div>
        </Card>

        <Card className="pad">
          <CardHead title="доступ в админку" caption="кого пускать — решает глобальная роль" />
          <div className="note">
            Отдельного списка допущенных у админки нет: внутрь пускает роль администратора
            у учётной записи 7cut. Выдаётся она не здесь — это сделано намеренно, чтобы
            раздача глобальных прав не превращалась в кнопку.
          </div>
        </Card>
      </div>

      {/* Убрано под спойлер: ручной запуск делает то же, что ночная задача —
          списывает деньги, рассылает письма, удаляет данные. */}
      <details className="card pad fold" style={{ marginTop: 12 }}>
        <summary>
          <span className="fold-t">фоновые задачи</span>
          <span className="fold-c">сюда заходить обычно не нужно — сервис делает это сам</span>
        </summary>
        <div className="fold-b">
          <p className="capt">
            Эти задачи идут по расписанию без участия человека: списывают деньги, закрывают триалы,
            шлют письма, удаляют данные по сроку. Запускать вручную нужно в двух случаях: задача
            не отработала ночью, или вы руками поправили данные и хотите применить результат сразу.
          </p>
          <div className="note warn" style={{ marginBottom: 14 }}>
            Запуск вручную делает всё то же, что ночью: списывает деньги с карт, рассылает письма,
            удаляет данные. Отменить нельзя.
          </div>
          <div className="list">
            {JOBS.map((j) => (
              <div className="row" key={j.key}>
                <span className="rn">{j.title}<div className="t-sub">{j.schedule}</div></span>
                {canEdit ? <RunButton job={j} /> : null}
              </div>
            ))}
          </div>
          <div className="note" style={{ marginTop: 14 }}>
            Чего здесь не хватает: когда задача отработала в последний раз и чем кончилась.
            Сервер историю запусков не хранит, поэтому показать нечего.
          </div>
        </div>
      </details>
    </>
  );
}

/* Запуск в два клика: случайное нажатие ничего не делает. */
function RunButton({ job }) {
  const [state, setState] = useState('idle');

  if (state === 'done') return <button className="btn small" disabled style={{ color: 'var(--ok)' }}>запущено</button>;
  if (state === 'busy') return <button className="btn small" disabled>запускаем…</button>;

  if (state === 'armed') {
    setTimeout(() => setState((s) => (s === 'armed' ? 'idle' : s)), 4000);
    return (
      <button className="btn small danger" onClick={async () => {
        setState('busy');
        try { await api.runJob(job.key); setState('done'); setTimeout(() => setState('idle'), 2500); }
        catch { setState('idle'); }
      }}>точно запустить?</button>
    );
  }

  return <button className="btn small" onClick={() => setState('armed')}>запустить</button>;
}
